import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { STAGES, formatINR, STAGE_DOT } from "@/lib/constants";
import { LeadDetail } from "@/components/LeadDetail";

export default function Pipeline() {
  const [leads, setLeads] = useState([]);
  const [lenders, setLenders] = useState([]);
  const [partners, setPartners] = useState([]);
  const [stages, setStages] = useState(STAGES);
  const [detailLead, setDetailLead] = useState(null);

  const load = useCallback(() => {
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    api.get("/lenders").then((r) => setLenders(r.data));
    api.get("/partners").then((r) => setPartners(r.data));
    api.get("/meta").then((r) => setStages(r.data.stages || STAGES)).catch(() => {});
  }, [load]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight">Loan Pipeline</h1>
        <p className="text-sm text-slate-500 mt-1">Track every application from lead to disbursement</p>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4">
        {stages.map((stage) => {
          const items = leads.filter((l) => l.status === stage);
          const total = items.reduce((s, l) => s + (l.loan_amount || 0), 0);
          return (
            <div key={stage} data-testid={`pipeline-column-${stage.replace(/\s+/g, "-").toLowerCase()}`}
              className="min-w-[280px] w-72 flex-shrink-0 bg-slate-100/70 rounded-xl p-4 border border-slate-200 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${STAGE_DOT[stage] || "bg-slate-400"}`} />
                  <span className="text-sm font-semibold text-slate-700">{stage}</span>
                </div>
                <span className="text-xs font-mono bg-white text-slate-500 px-2 py-0.5 rounded-full border border-slate-200">{items.length}</span>
              </div>
              <div className="text-xs text-slate-400 font-mono -mt-1">{formatINR(total)}</div>
              <div className="space-y-2 flex-1 min-h-[80px]">
                {items.map((l) => (
                  <button key={l.id} data-testid={`lead-pipeline-card-${l.id}`} onClick={() => setDetailLead(l)}
                    className="w-full text-left p-3 rounded-lg bg-white border border-slate-200 hover:border-emerald-400 hover:shadow-md transition-all">
                    <div className="text-sm font-semibold text-slate-800 truncate">{l.customer_name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{l.loan_type}</div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs font-mono text-emerald-600 font-semibold">{formatINR(l.loan_amount)}</span>
                      <span className="text-[10px] text-slate-400 truncate max-w-[110px]">{l.partner_name || "Unassigned"}</span>
                    </div>
                  </button>
                ))}
                {items.length === 0 && <div className="text-xs text-slate-300 text-center py-6">No leads</div>}
              </div>
            </div>
          );
        })}
      </div>

      {detailLead && (
        <LeadDetail lead={detailLead} lenders={lenders} partners={partners}
          onClose={() => setDetailLead(null)} onChange={load} />
      )}
    </div>
  );
}
